import { hueVars, type NxHue } from '../lib/hue';

/**
 * Intent tone of an accent — a panel's edge, a card's priority strip, an
 * avatar's fill: the colour MEANS something (high priority = danger, done =
 * success). When the colour only tells groups apart, use a category hue
 * (`NxHue`) instead.
 */
export type NxAccentTone = 'brand' | 'neutral' | 'info' | 'success' | 'warning' | 'danger';

/**
 * How much of an accent a card or panel shows (NxPanel, NxItemCard,
 * NxKpiCard — the same prop `accentLevel` on all three):
 *
 * - `muted` — no accent; the thing steps back: muted text, less saturation.
 * - `tint`  — no strip; only the component's own colour spot takes the
 *   accent (NxItemCard: the icon tile, NxKpiCard: the figure and its corner
 *   wash; NxPanel, which has no spot, tints its header band instead).
 * - `edge`  — the strip on the leading edge, plus the colour spot (default).
 * - `fill`  — strip and colour spot, and a light wash over the surface.
 *
 * The colour comes from `accent` (any colour token), `hue` or `tone`; `muted`
 * drops it. `accentEmphasis` (0–1) scales the strip's width — 0 is no strip —
 * and how strongly a token accent tints.
 */
export type NxAccentLevel = 'muted' | 'tint' | 'edge' | 'fill';

export const NX_ACCENT_LEVELS: readonly NxAccentLevel[] = ['muted', 'tint', 'edge', 'fill'];

// A colour token's name without the `--nxt-` prefix: `warning-bg`, `hue-teal`, `chart-3`.
const TOKEN_NAME = /^[a-z][a-z0-9]*(?:-[a-z0-9]+)*$/;

/**
 * `var(--nxt-<name>)` for the name of a colour token (`'warning-bg'`,
 * `'hue-teal'`, `'chart-3'`; no `--nxt-` prefix), `null` for anything that is
 * not such a name — so data from a document never reaches the CSS unchecked.
 */
export function nxtColorTokenVar(name: string | null | undefined): string | null {
  return name && TOKEN_NAME.test(name) ? `var(--nxt-${name})` : null;
}

/**
 * Local custom properties pointing a component at one accent, in the shape
 * `hueVars` gives a hue: `<prefix>` (solid fill, strips), `<prefix>-on` (text
 * on the solid fill), `<prefix>-fg` (text/icon on the tinted fill),
 * `<prefix>-subtle-bg` (tinted fill), `<prefix>-border`. A hue wins over a
 * tone; with neither the result is empty, so the component keeps its default.
 */
export function accentVars(
  tone: NxAccentTone | null | undefined,
  hue: NxHue | null | undefined,
  prefix = '--accent'
): Record<string, string> {
  if (hue) return hueVars(hue, prefix);
  if (!tone) return {};
  const t = `--nxt-${tone}`;
  return {
    [prefix]: `var(${t}-bg)`,
    [`${prefix}-on`]: `var(${t}-fg)`,
    [`${prefix}-fg`]: `var(${t}-subtle-fg)`,
    [`${prefix}-subtle-bg`]: `var(${t}-subtle-bg)`,
    [`${prefix}-border`]: `var(${t}-border)`,
  };
}

/**
 * The same properties from any colour token (`nxtColorTokenVar`), for accents a
 * document picks freely — a presentation layer's colour. A token carries no
 * partner tones, so they are mixed from it: `-fg` leans towards the text
 * colour (icons keep their contrast in light and dark), `-subtle-bg` is a wash
 * whose strength follows `<prefix>-emphasis` (set by `resolveAccent`), `-on`
 * is the surface colour. Not a token name → empty.
 */
export function tokenAccentVars(
  token: string | null | undefined,
  prefix = '--accent'
): Record<string, string> {
  const color = nxtColorTokenVar(token);
  if (!color) return {};
  return {
    [prefix]: color,
    [`${prefix}-on`]: 'var(--nxt-surface-bg)',
    [`${prefix}-fg`]: `color-mix(in oklab, ${color} 80%, var(--nxt-fg))`,
    [`${prefix}-subtle-bg`]: `color-mix(in oklab, ${color} calc(6% + 10% * var(${prefix}-emphasis)), var(--nxt-surface-raised-bg))`,
    [`${prefix}-border`]: `color-mix(in oklab, ${color} 35%, transparent)`,
  };
}

/** The accent props NxPanel, NxItemCard and NxKpiCard share. */
export interface NxAccentInput {
  tone?: NxAccentTone | null;
  hue?: NxHue | null;
  /** A colour token's name (`nxtColorTokenVar`); wins over `hue` and `tone`. */
  accent?: string | null;
  accentLevel?: NxAccentLevel | null;
  /** 0–1: the strip's width and a token accent's tint strength. Default 1. */
  accentEmphasis?: number | null;
}

export interface NxResolvedAccent {
  /** Custom properties for `:style`: the accent's colours and `<prefix>-emphasis`. */
  vars: Record<string, string>;
  /** Whether a colour shows: given (token, hue or tone) and the level is not `muted`. */
  hasColor: boolean;
  /** Whether the colour came from a token (`accent`). */
  fromToken: boolean;
  /** The level to show; `muted` also without a colour. */
  level: NxAccentLevel;
}

function clampEmphasis(value: number | null | undefined): number {
  if (value === null || value === undefined || !Number.isFinite(value)) return 1;
  return Math.min(1, Math.max(0, value));
}

/**
 * One accent for a component: the token wins over the hue, the hue over the
 * tone. The level defaults to `edge`; an unknown level reads as `edge` too;
 * `muted` drops the colour.
 */
export function resolveAccent(input: NxAccentInput, prefix = '--accent'): NxResolvedAccent {
  const level =
    input.accentLevel && NX_ACCENT_LEVELS.includes(input.accentLevel) ? input.accentLevel : 'edge';
  const tokenVars = level === 'muted' ? {} : tokenAccentVars(input.accent, prefix);
  const fromToken = Object.keys(tokenVars).length > 0;
  const colorVars =
    fromToken || level === 'muted' ? tokenVars : accentVars(input.tone, input.hue, prefix);
  const hasColor = Object.keys(colorVars).length > 0;
  return {
    vars: hasColor
      ? { ...colorVars, [`${prefix}-emphasis`]: String(clampEmphasis(input.accentEmphasis)) }
      : {},
    hasColor,
    fromToken,
    level,
  };
}

/** A component-wide opacity (0–1) as a style entry; nothing at 1 or for invalid input. */
export function opacityStyle(opacity: number | null | undefined): Record<string, string> {
  if (opacity === null || opacity === undefined || !Number.isFinite(opacity)) return {};
  const value = Math.min(1, Math.max(0, opacity));
  return value >= 1 ? {} : { opacity: String(value) };
}
